import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Space, Table, Tag, Typography } from 'antd';
import { Plus, Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { deleteConfigurableDefinition, listConfigurableDefinitions } from '../services/configurableProductApi';

const ConfigurableProductsList = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listConfigurableDefinitions(getAuthHeaders());
      setItems(data?.items || []);
    } catch (err) {
      window.alert(err.message || 'Unable to load configurable products');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const removeItem = async (id) => {
    const ok = window.confirm('Delete this configurable product?');
    if (!ok) return;
    try {
      await deleteConfigurableDefinition(id, getAuthHeaders());
      await load();
    } catch (err) {
      window.alert(err.message || 'Unable to delete configurable product');
    }
  };

  return (
    <div className="container-fluid py-3">
      <Card className="mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Space>
            <Wrench size={18} />
            <Typography.Title level={4} className="mb-0">Configurable Products</Typography.Title>
          </Space>
          <Button type="primary" icon={<Plus size={14} />} onClick={() => navigate('/products/configurable/new')}>
            Add New
          </Button>
        </div>
      </Card>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          pagination={{ pageSize: 20 }}
          columns={[
            {
              title: 'Product',
              key: 'product',
              render: (_, row) => (
                <div>
                  <div className="fw-semibold">{row.name || row.product_code}</div>
                  <div className="small text-muted">{row.product_code}</div>
                </div>
              ),
            },
            {
              title: 'Steps',
              dataIndex: 'steps_count',
              key: 'steps_count',
              width: 100,
            },
            {
              title: 'Part Number Rule',
              key: 'part_rule',
              width: 220,
              render: (_, row) =>
                row.include_parent_product_code !== false ? (
                  <Tag color="blue">Include Parent Product ID</Tag>
                ) : (
                  <Tag>Exclude Parent Product ID</Tag>
                ),
            },
            {
              title: 'Status',
              key: 'status',
              width: 220,
              render: (_, row) => (
                <Space size={4} wrap>
                  <Tag color={row.workflow_state === 'published' ? 'green' : 'gold'}>
                    {row.workflow_state === 'published' ? 'Published' : 'Draft'}
                  </Tag>
                  <Tag color={row.is_ready ? 'blue' : 'default'}>
                    {row.is_ready ? 'Ready' : 'Not Ready'}
                  </Tag>
                </Space>
              ),
            },
            {
              title: 'Actions',
              key: 'actions',
              width: 320,
              render: (_, row) => (
                <Space size={6}>
                  <Button size="small" onClick={() => navigate(`/products/configurable/edit/${row.id}`)}>Edit</Button>
                  <Button
                    size="small"
                    type="primary"
                    disabled={!row.has_published_version || row.is_active === false}
                    onClick={() => navigate(`/products/configurable/use/${encodeURIComponent(row.published_product_code || row.product_code)}`)}
                  >
                    Open Builder
                  </Button>
                  <Button size="small" danger onClick={() => removeItem(row.id)}>Delete</Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default ConfigurableProductsList;
